import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap';
import FetchContainer from 'Utils/FetchContainer';
import CandidateSourcesPanel from './CandidateSourcesPanel';
import { getOverview, getDiscoverFanout, cancelDiscoverFanout } from 'DataDiscovery/discoverApi';
import { getLandscapeHomeSummary } from 'SensitiveDataLandscape/homeSummaryApi';

/**
 * Full-page candidate sources view for a Folder Search run
 * (/results/:search/candidate-sources). The rows come from the run's
 * scan_meta.candidate_sources; CandidateSourcesPanel supplies the coverage logic
 * (coverageIndex keyed rootPath|datasourceType), the Discover / Running… /
 * View-risk-profile action cell, and the StartDiscoverDialog prefill unchanged —
 * this page just adds search + sortable headers and full-height layout.
 */
const CandidateSourcesPage = (props) => {
  const params = useParams();
  const history = useHistory();
  const searchid = params.search;

  const [loading, setLoading] = useState(true);
  const [candidateSources, setCandidateSources] = useState([]);
  const [discoveryBySourceId, setDiscoveryBySourceId] = useState({});
  const [fanoutRows, setFanoutRows] = useState([]);
  const [error, setError] = useState(null);

  // Same source resolution as OverviewPanel: id preferred over name.
  const connectionDetails = useMemo(() => {
    const list = props.connections || [];
    if (props.connectionId != null) {
      const byId = list.find((c) => c?.id === props.connectionId);
      if (byId) return byId;
    }
    if (typeof props.connection === 'string' && props.connection) {
      return list.find((c) => c?.name === props.connection) || null;
    }
    return null;
  }, [props.connections, props.connection, props.connectionId]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      try {
        const response = await getOverview(searchid);
        if (!cancelled) setCandidateSources(response?.scan_meta?.candidate_sources || []);
      } catch (e) {
        console.log(e);
        if (!cancelled) setError(e?.message || 'Failed to load candidate sources');
      }
      setLoading(false);
    };
    if (searchid) init();
    return () => { cancelled = true; };
  }, [searchid]);

  useEffect(() => {
    if (candidateSources.length === 0) return;
    getLandscapeHomeSummary().then((summary) => {
      const map = {};
      ((summary && summary.sources) || []).forEach((row) => {
        if (row && row.id != null) map[row.id] = row;
      });
      setDiscoveryBySourceId(map);
    }).catch(() => {});
  }, [candidateSources.length]);

  // "Discover everything" ledger — empty list when the run did not opt in, so the
  // panel keeps its plain candidate behaviour. Polled while any row is still queued
  // or freshly started so the Queued → Running transitions show without a reload.
  useEffect(() => {
    if (!searchid || candidateSources.length === 0) return undefined;
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const rows = await getDiscoverFanout(searchid);
        if (cancelled) return;
        setFanoutRows(Array.isArray(rows) ? rows : []);
        const hasPending = (rows || []).some(
          (r) => r.state === 'PENDING' || r.state === 'REUSED_SOURCE' || r.state === 'STARTED');
        if (hasPending) timer = setTimeout(load, 15000);
      } catch (e) {
        // Older platform without the endpoint, or transient failure — plain panel.
        if (!cancelled) setFanoutRows([]);
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [searchid, candidateSources.length]);

  const handleCancelFanout = async () => {
    try {
      const rows = await cancelDiscoverFanout(searchid);
      setFanoutRows(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.log('fanout cancel', e);
    }
  };

  return (
    <FetchContainer fetchingData={loading} error={error} absolute={true}>
      <div data-testid='candidate-sources-page'>
        <Row className='widget-row'>
          <Col className='text-left'>
            <Button
              variant='link'
              size='sm'
              style={{ padding: 0 }}
              onClick={() => history.push(`/results/${searchid}`)}
            >
              ← Back to overview
            </Button>
          </Col>
        </Row>
        <Row className='mt-2'>
          <Col>
            {candidateSources.length === 0 && !loading ? (
              <div className='text-center' style={{ padding: 48, color: '#8A92A0' }}>
                This run detected no candidate data sources.
              </div>
            ) : (
              <CandidateSourcesPanel
                candidateSources={candidateSources}
                connections={props.connections}
                vfsScheme={connectionDetails?.vfsScheme || connectionDetails?.config?.vfsScheme || 'LOCAL'}
                parentSourceName={connectionDetails?.name || (typeof props.connection === 'string' ? props.connection : '')}
                parentSourceId={connectionDetails?.id ?? null}
                discoveryBySourceId={discoveryBySourceId}
                fanoutRows={fanoutRows}
                onCancelFanout={handleCancelFanout}
                searchable
                sortable
              />
            )}
          </Col>
        </Row>
      </div>
    </FetchContainer>
  );
};

export default CandidateSourcesPage;
